import { addDays, eachDayOfInterval } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RangePicker } from '@/entities/range-picker';
import { useToastActions } from '@/entities/toast';
import { LazyReducer } from '@/shared/lib/components/lazy-reducer';
import { useMatchMedia } from '@/shared/lib/hooks/use-match-media.hook';
import { useTimezone } from '@/shared/lib/hooks/use-timezone.hook';
import { Box } from '@/shared/ui/box';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

import { useCreateHistogramMutation } from '../../api/histogram-form.api';
import { useCreateHistogramForm } from '../../model/selectors/select-create-histogram-form.selector';
import { useIsValidCreateHistogramForm } from '../../model/selectors/select-is-valid-create-histogram-form.selector';
import { parseDateCreating } from '../../model/services/parse-date-creating.service';
import { useCreateHistogramFormActions } from '../../model/slice/create-histogram-form.slice';
import { reducers } from './create-histogram-form.data';
import { CreateHistogramFormProps } from './create-histogram-form.types';

const CreateHistogramFormComponent = ({ uuid }: CreateHistogramFormProps) => {
	const { resetForm, setName, setStartDate, setEndDate, setValidationError } = useCreateHistogramFormActions();
	const { name, startDate, endDate, validationError } = useCreateHistogramForm();
	const [createHistogramMutation, { isLoading }] = useCreateHistogramMutation();
	const { addToast } = useToastActions();
	const isValid = useIsValidCreateHistogramForm();
	const { t } = useTranslation('yachts');
	const isTablet = useMatchMedia('tabletAndMobile');
	const { timezone } = useTimezone();
	const start = addDays(utcToZonedTime(new Date(), timezone), 1);
	const end = addDays(utcToZonedTime(new Date(), timezone), 36500);
	const highlightedDates = useMemo(() => {
		return eachDayOfInterval({ start, end });
	}, [start, end]);

	const filterTime = useCallback(
		(value: Date) => {
			const currentDate = utcToZonedTime(new Date(), timezone);
			const selectedDate = new Date(value);

			return currentDate.getTime() > selectedDate.getTime();
		},
		[timezone],
	);

	const onReset = useCallback(() => {
		resetForm();
	}, [resetForm]);

	const onCreate = useCallback(() => {
		if (isValid && startDate && endDate) {
			const start = parseDateCreating(startDate, timezone);
			const end = parseDateCreating(endDate, timezone);

			if (start < end) {
				createHistogramMutation({
					name,
					start,
					end,
					uuid,
				});
			} else {
				addToast({
					type: 'error',
					title: t('toast.error.title'),
					message: t('toast.error.dateCollapsed'),
				});
			}
		} else {
			addToast({ type: 'error', title: t('toast.error.title'), message: t('toast.error.validation') });
			setValidationError();
		}
	}, [addToast, createHistogramMutation, endDate, isValid, name, setValidationError, startDate, t, timezone, uuid]);

	return (
		<LazyReducer reducers={reducers}>
			<Box direction="column" gap={32}>
				<Input
					label={t('track.field.name')}
					placeholder={t('track.field.name.placeholder')}
					onChange={setName}
					value={name}
					error={{ show: validationError.name }}
					minWidth="m"
					maxWidth="full"
				/>
				<RangePicker
					startDate={startDate}
					onChangeStartDate={(date) => setStartDate({ date, timezone })}
					endDate={endDate}
					onChangeEndDate={(date) => setEndDate({ date, timezone })}
					startDateLabel={t('track.field.startDate.label')}
					endDateLabel={t('track.field.endDate.label')}
					startDateError={validationError.startDate}
					endDateError={validationError.endDate}
					marginLeft="none"
					marginBottom="0"
					maxWidth="full"
					padding="0"
					flexWrap={isTablet ? 'wrap' : 'nowrap'}
					flex={1}
					showTimeSelect
					filterTime={filterTime}
					highlightDates={highlightedDates}
					isNextDisabled
				/>
				<Box gap={24}>
					<Button label={t('track.button.create')} onClick={onCreate} isLoading={isLoading} size="full" />
					<Button label={t('track.button.reset')} variant="outlined" onClick={onReset} size="full" />
				</Box>
			</Box>
		</LazyReducer>
	);
};

export default memo(CreateHistogramFormComponent);
